import React from "react";
import { formatarParaReais } from "../../services/utilitarios";
import { tokens } from "../modal-form/tokens";

// Espelha Locacoes.Domain.Dto.OrigemAssinaturaAsaas
export enum OrigemAssinaturaAsaas {
    DestaLocacao = 0,
    DeOutraLocacao = 1,
    Orfa = 2,
}

export interface AssinaturaResumo {
    id: string;
    customer?: string;
    value: number;
    cycle?: string;
    nextDueDate?: string;
    endDate?: string;
    status?: string;
    description?: string;
    externalReference?: string;
    billingType?: string;
    interestPercentage?: number;
    fineValue?: number;
    fineType?: string;
}

export interface AssinaturaClassificada {
    assinatura: AssinaturaResumo;
    origem: OrigemAssinaturaAsaas;
    podeReaproveitar: boolean;
    motivoBloqueioDescricao?: string;
    faturasVencidasEmAberto?: number;
    valorFaturasVencidasEmAberto?: number;
    faturasFuturasJaGeradas?: number;
    cobrancasPagas?: { dueDate: string; value: number }[];
    /** Vencimento da cobrança mais antiga desta assinatura que nenhum contrato usa: quando a cobrança começou. */
    primeiroVencimentoLivre?: string;
    idLocacaoDona?: string;
    placaDaLocacaoDona?: string;
    statusDaLocacaoDona?: string;
}

export interface ClassificacaoAssinaturas {
    assinaturas: AssinaturaClassificada[];
    consultaIndisponivel: boolean;
    mensagemIndisponibilidade?: string;
}

interface Props {
    classificacao?: ClassificacaoAssinaturas | null;
    carregando?: boolean;
    /** Dados da locação: é o que a assinatura passará a valer. */
    valorAluguel?: number;
    cicloAsaas?: string;
    /** id da assinatura a reaproveitar, ou null para criar uma nova. */
    escolha: string | null;
    onEscolher: (idAssinatura: string | null) => void;
    /** true = reescrever as faturas em aberto com os dados da nova locação. */
    atualizarFaturas: boolean;
    onAtualizarFaturas: (valor: boolean) => void;
    importarPagasAnteriores: boolean;
    onImportarPagasAnteriores: (valor: boolean) => void;
    dataInicio?: string;
    /**
     * Quantas cobranças de cada assinatura estão na página de Cobranças para o operador trazer. É o que
     * permite dizer, sobre a assinatura que NÃO vira o aluguel, onde estão os vencimentos dela.
     */
    cobrancasPorAssinatura?: Record<string, number>;
    /**
     * Versão enxuta, para quando a escolha divide a tela com outros campos (modal de vincular):
     * corta as explicações e os detalhes secundários, deixando só a decisão.
     */
    compacto?: boolean;
}

const CICLO_TEXTO: Record<string, string> = {
    WEEKLY: "por semana",
    BIWEEKLY: "a cada quinzena",
    MONTHLY: "por mês",
    BIMONTHLY: "a cada 2 meses",
    QUARTERLY: "a cada 3 meses",
    SEMIANNUALLY: "a cada 6 meses",
    YEARLY: "por ano",
};

const textoCiclo = (ciclo?: string) => (ciclo ? CICLO_TEXTO[ciclo] || ciclo.toLowerCase() : "");

const semAcento = (texto?: string) =>
    (texto || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();

const PALAVRAS_DE_ALUGUEL = ["ALUGUEL", "ALUGUER", "LOCACAO"];

/** Assinatura que cobra o aluguel, pela descrição (as criadas pelo sistema são "ALUGUEL - placa"). */
export const pareceAssinaturaDeAluguel = (assinatura?: AssinaturaResumo) =>
    PALAVRAS_DE_ALUGUEL.some((p) => semAcento(assinatura?.description).includes(p));

/**
 * Qual assinatura deve vir marcada para virar o aluguel desta locação. Só sugere quando a resposta é
 * inequívoca: uma única candidata com cara de aluguel pela descrição ou, sem nenhuma assim, uma única
 * com o valor do aluguel informado. Com dúvida, nada vem marcado e o operador escolhe. Errar aqui
 * reconfiguraria a assinatura da manutenção como se fosse o aluguel.
 */
export function sugerirAssinaturaDoAluguel(candidatas: AssinaturaClassificada[], valorAluguel?: number): string | null {
    const lista = candidatas || [];
    const deAluguel = lista.filter((c) => pareceAssinaturaDeAluguel(c.assinatura));
    if (deAluguel.length === 1) return deAluguel[0].assinatura.id;
    if (deAluguel.length > 1) return null;

    if (valorAluguel && valorAluguel > 0) {
        const mesmoValor = lista.filter((c) => Math.abs(Number(c.assinatura.value) - valorAluguel) < 0.01);
        if (mesmoValor.length === 1) return mesmoValor[0].assinatura.id;
    }
    return null;
}

const ISO = (data?: string) => (data ? String(data).substring(0, 10) : "");

/**
 * Recua k ciclos a partir de uma data-base, sempre calculando da BASE (não encadeado): recuar 31/10
 * mês a mês dá 30/09, 31/08, 31/07, sem perder o dia 31 ao passar por um mês curto.
 */
const recuarCiclos = (base: string, ciclo: string | undefined, k: number): string | null => {
    const [ano, mes, dia] = base.split("-").map(Number);
    if (!ano || !mes || !dia) return null;
    if (ciclo === "WEEKLY") {
        return new Date(Date.UTC(ano, mes - 1, dia - 7 * k)).toISOString().substring(0, 10);
    }
    if (ciclo === "MONTHLY") {
        const alvo = new Date(Date.UTC(ano, mes - 1 - k, 1));
        const ultimoDia = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
        alvo.setUTCDate(Math.min(dia, ultimoDia));
        return alvo.toISOString().substring(0, 10);
    }
    return null;
};

/**
 * Datas que a locação herda da assinatura reaproveitada. A retirada é quando a cobrança deste cliente
 * começou (a cobrança livre mais antiga). O primeiro pagamento segue o calendário ATUAL da assinatura:
 * é o próximo vencimento recuado ciclo a ciclo até a retirada, assim as parcelas do contrato caem nos
 * mesmos dias das cobranças que o Asaas já emitiu, mesmo que o dia de vencimento tenha mudado no meio
 * do caminho. O encerramento é o fim da assinatura, quando ela tem um.
 */
export function datasSugeridasPelaAssinatura(item?: AssinaturaClassificada): { inicio?: string; primeiroPagamento?: string; termino?: string } {
    const assinatura = item?.assinatura;
    if (!assinatura) return {};

    const proximo = ISO(assinatura.nextDueDate);
    const primeiroLivre = ISO(item?.primeiroVencimentoLivre);
    const termino = ISO(assinatura.endDate) || undefined;

    // Retirada no histórico só onde o cronograma do sistema anda igual ao do Asaas: semanal (7 dias)
    // e mensal com vencimento até o dia 28. Na quinzenal o sistema avança 15 dias e o Asaas 14, e nos
    // dias 29 a 31 o mês curto corta o dia: começar no passado tiraria a assinatura do calendário
    // dela. Nesses casos vale o próximo vencimento, como antes.
    const diaDoProximo = Number(proximo.substring(8, 10));
    const calendarioCompativel = !!proximo
        && (assinatura.cycle === "WEEKLY" || (assinatura.cycle === "MONTHLY" && diaDoProximo <= 28));

    if (!primeiroLivre || !calendarioCompativel || primeiroLivre >= proximo) {
        const data = proximo || primeiroLivre || undefined;
        return { inicio: data, primeiroPagamento: data, termino };
    }

    let primeiroPagamento = proximo;
    // Teto de iterações: uma data estranha não pode travar a tela.
    for (let k = 1; k <= 600; k++) {
        const anterior = recuarCiclos(proximo, assinatura.cycle, k);
        if (!anterior || anterior < primeiroLivre) break;
        primeiroPagamento = anterior;
    }

    return { inicio: primeiroLivre, primeiroPagamento, termino };
}

const formatarData = (data?: string) => {
    if (!data) return "";
    const [ano, mes, dia] = String(data).substring(0, 10).split("-");
    return dia && mes && ano ? `${dia}/${mes}/${ano}` : String(data);
};

const aviso: React.CSSProperties = {
    padding: tokens.spacingMd,
    borderRadius: tokens.radiusMd,
    fontSize: tokens.fontSm,
    background: tokens.warningBg,
    border: `1px solid ${tokens.warningColor}`,
    color: tokens.textSecondary,
};

const info: React.CSSProperties = {
    padding: tokens.spacingMd,
    borderRadius: tokens.radiusMd,
    fontSize: tokens.fontSm,
    background: tokens.bgLight,
    border: `1px solid ${tokens.borderLight}`,
    color: tokens.textSecondary,
};

function Opcao({
    marcada, onSelecionar, titulo, children, compacto,
}: { marcada: boolean; onSelecionar: () => void; titulo: string; children?: React.ReactNode; compacto?: boolean }) {
    return (
        <label
            onClick={onSelecionar}
            style={{
                display: "block",
                padding: compacto ? tokens.spacingMd : tokens.spacingLg,
                marginBottom: compacto ? tokens.spacingXs : tokens.spacingSm,
                borderRadius: tokens.radiusLg,
                cursor: "pointer",
                background: marcada ? tokens.primaryLight : tokens.bgWhite,
                border: `2px solid ${marcada ? tokens.primaryColor : tokens.borderDefault}`,
                boxShadow: marcada ? tokens.shadowSm : "none",
                transition: tokens.transitionBase,
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: tokens.spacingSm }}>
                <input type="radio" name="escolhaAssinaturaAsaas" checked={marcada} onChange={onSelecionar} />
                <span style={{ fontWeight: 600, fontSize: tokens.fontBase, color: tokens.textPrimary }}>{titulo}</span>
            </div>
            {children && <div style={{ marginTop: tokens.spacingSm, marginLeft: "1.6rem" }}>{children}</div>}
        </label>
    );
}

function DeParaValor({ de, deCiclo, para, paraCiclo }: { de: number; deCiclo?: string; para?: number; paraCiclo?: string }) {
    const celula = (rotulo: string, valor: string, complemento: string, destaque: boolean) => (
        <div style={{ flex: 1, minWidth: 130 }}>
            <div style={{ fontSize: tokens.fontXs, color: tokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em" }}>{rotulo}</div>
            <div style={{ fontSize: tokens.fontLg, fontWeight: 600, color: destaque ? tokens.primaryColor : tokens.textSecondary }}>{valor}</div>
            <div style={{ fontSize: tokens.fontXs, color: tokens.textMuted }}>{complemento}</div>
        </div>
    );

    return (
        <div style={{ display: "flex", alignItems: "center", gap: tokens.spacingMd, flexWrap: "wrap" }}>
            {celula("Hoje cobra", formatarParaReais(de), textoCiclo(deCiclo), false)}
            <div style={{ fontSize: tokens.fontXl, color: tokens.textMuted, lineHeight: 1 }}>›</div>
            {para && para > 0
                ? celula("Passa a cobrar", formatarParaReais(para), textoCiclo(paraCiclo), true)
                : celula("Passa a cobrar", "o valor desta locação", "como você definir nos próximos passos", true)}
        </div>
    );
}

/**
 * Escolha de assinatura do Asaas na vinculação e na criação de locação.
 *
 * O ponto da tela: decidir ANTES de qualquer escrita. Antes, a existência de uma assinatura antiga só
 * aparecia depois de a nova já ter sido criada, e a única saída oferecida era apagar a antiga, junto
 * com os boletos que o locatário já tinha em mãos.
 */
export default function EscolhaAssinaturaAsaas({
    classificacao, carregando, valorAluguel, cicloAsaas, escolha, onEscolher, atualizarFaturas, onAtualizarFaturas,
    importarPagasAnteriores, onImportarPagasAnteriores, dataInicio, compacto, cobrancasPorAssinatura,
}: Props) {
    if (carregando) {
        return <div style={info}>Consultando as assinaturas deste cliente no Asaas...</div>;
    }

    if (!classificacao) return null;

    if (classificacao.consultaIndisponivel) {
        return (
            <div style={aviso}>
                <strong>Não foi possível consultar o Asaas agora.</strong>
                <div style={{ marginTop: tokens.spacingXs }}>
                    Se este cliente já tiver uma assinatura ativa, continuar pode gerar cobrança duplicada.
                    Confira no painel do Asaas antes de prosseguir.
                </div>
            </div>
        );
    }

    const lista = classificacao.assinaturas || [];
    const deOutrasLocacoes = lista.filter((a) => a.origem === OrigemAssinaturaAsaas.DeOutraLocacao);
    const reaproveitaveis = lista.filter((a) => a.origem !== OrigemAssinaturaAsaas.DeOutraLocacao && a.podeReaproveitar);
    const bloqueadas = lista.filter((a) => a.origem !== OrigemAssinaturaAsaas.DeOutraLocacao && !a.podeReaproveitar);

    if (!lista.length) return null;

    const selecionada = reaproveitaveis.find((a) => a.assinatura.id === escolha);
    const inicio = dataInicio ? String(dataInicio).substring(0, 10) : "";
    // Com o primeiro vencimento livre conhecido, a locação começa nele: toda cobrança paga ANTERIOR a
    // ele já pertence a outro contrato do sistema, e perguntar se deve "trazer" só confundiria.
    const pagasEmQuestao = selecionada?.primeiroVencimentoLivre ? [] : (selecionada?.cobrancasPagas || [])
        .filter((c) => !inicio || String(c.dueDate).substring(0, 10) < inicio);
    const datasSugeridas = datasSugeridasPelaAssinatura(selecionada);
    const outrasAtivas = selecionada ? reaproveitaveis.filter((a) => a.assinatura.id !== selecionada.assinatura.id) : [];
    const temAlternativas = reaproveitaveis.length > 1;
    const valorPagasEmQuestao = pagasEmQuestao.reduce((total, c) => total + (Number(c.value) || 0), 0);

    return (
        <div>
            {reaproveitaveis.length > 0 && (
                <>
                    <div style={{ fontSize: compacto ? tokens.fontSm : tokens.fontBase, color: tokens.textPrimary, marginBottom: compacto ? tokens.spacingXs : tokens.spacingMd }}>
                        {reaproveitaveis.length > 1
                            ? `Este cliente tem ${reaproveitaveis.length} assinaturas ativas no Asaas que não estão vinculadas a nenhuma locação.`
                            : "Este cliente já tem uma assinatura ativa no Asaas que não está vinculada a nenhuma locação."}{" "}
                        O que você quer fazer?
                    </div>

                    {reaproveitaveis.map((item) => {
                        const a = item.assinatura;
                        return (
                            <Opcao
                                key={a.id}
                                marcada={escolha === a.id}
                                compacto={compacto}
                                onSelecionar={() => onEscolher(a.id)}
                                titulo={temAlternativas
                                    ? `Aproveitar esta: ${a.description ? `${a.description}, ` : ""}${formatarParaReais(a.value)} ${textoCiclo(a.cycle)}`
                                    : "Aproveitar a assinatura que já existe"}
                            >
                                {temAlternativas && pareceAssinaturaDeAluguel(a) && (
                                    <div style={{ display: "inline-block", marginBottom: tokens.spacingXs, padding: "1px 8px", borderRadius: tokens.radiusSm, fontSize: tokens.fontXs, background: tokens.primaryLight, color: tokens.primaryColor, border: `1px solid ${tokens.primaryColor}` }}>
                                        Parece ser a do aluguel
                                    </div>
                                )}
                                <DeParaValor de={a.value} deCiclo={a.cycle} para={valorAluguel} paraCiclo={cicloAsaas} />

                                {compacto ? (
                                    <div style={{ marginTop: tokens.spacingXs, fontSize: tokens.fontXs, color: tokens.textMuted }}>
                                        O locatário mantém o mesmo boleto
                                        {(item.faturasFuturasJaGeradas || 0) === 1
                                            ? ", e a cobrança já emitida passa a valer o novo valor."
                                            : (item.faturasFuturasJaGeradas || 0) > 1
                                                ? `, e as ${item.faturasFuturasJaGeradas} cobranças já emitidas passam a valer o novo valor.`
                                                : "."}
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ marginTop: tokens.spacingSm, fontSize: tokens.fontSm, color: tokens.textSecondary }}>
                                            O locatário continua com o mesmo boleto e não recebe cobrança em dobro.
                                            O valor, a periodicidade e o vencimento desta assinatura entram no cadastro
                                            da locação, e você pode alterá-los antes de salvar.
                                        </div>

                                        <ul style={{ margin: `${tokens.spacingSm} 0 0 1rem`, padding: 0, fontSize: tokens.fontXs, color: tokens.textMuted }}>
                                            {a.description && !temAlternativas && <li>{a.description}</li>}
                                            {item.primeiroVencimentoLivre && <li>Cobrando este cliente desde {formatarData(item.primeiroVencimentoLivre)}</li>}
                                            {a.nextDueDate && <li>Próximo vencimento hoje: {formatarData(a.nextDueDate)}</li>}
                                            {a.endDate && <li>Termina em {formatarData(a.endDate)}</li>}
                                            {(item.faturasFuturasJaGeradas || 0) === 1 && (
                                                <li>A cobrança já emitida com o valor antigo passa a valer o valor desta locação.</li>
                                            )}
                                            {(item.faturasFuturasJaGeradas || 0) > 1 && (
                                                <li>
                                                    As {item.faturasFuturasJaGeradas} cobranças já emitidas com o valor antigo passam a
                                                    valer o valor desta locação.
                                                </li>
                                            )}
                                        </ul>
                                    </>
                                )}
                            </Opcao>
                        );
                    })}

                    <Opcao
                        marcada={escolha === null}
                        compacto={compacto}
                        onSelecionar={() => onEscolher(null)}
                        titulo="Criar uma nova assinatura"
                    >
                        <div style={{ fontSize: compacto ? tokens.fontXs : tokens.fontSm, color: compacto ? tokens.textMuted : tokens.textSecondary }}>
                            A assinatura antiga continua ativa no Asaas e o locatário segue recebendo as duas.
                        </div>
                    </Opcao>

                    {selecionada && !compacto && datasSugeridas.inicio && (
                        <div style={{ ...info, marginTop: tokens.spacingMd }}>
                            <div style={{ fontWeight: 600, color: tokens.textPrimary }}>As datas da locação vêm desta assinatura</div>
                            <ul style={{ margin: `${tokens.spacingXs} 0 0 1rem`, padding: 0 }}>
                                <li>Retirada em {formatarData(datasSugeridas.inicio)}{datasSugeridas.inicio !== ISO(selecionada.assinatura.nextDueDate) ? ", quando a cobrança começou" : ""}</li>
                                {datasSugeridas.primeiroPagamento && <li>Primeiro pagamento em {formatarData(datasSugeridas.primeiroPagamento)}</li>}
                                {datasSugeridas.termino && <li>Encerramento em {formatarData(datasSugeridas.termino)}, fim da assinatura</li>}
                            </ul>
                            <div style={{ marginTop: tokens.spacingXs, fontSize: tokens.fontXs, color: tokens.textMuted }}>
                                As cobranças que ela já emitiu entram como parcelas do contrato. Você pode ajustar as datas no cadastro.
                            </div>
                        </div>
                    )}

                    {outrasAtivas.length > 0 && (
                        <div style={{ ...aviso, marginTop: tokens.spacingMd }}>
                            {outrasAtivas.map((item) => {
                                const quantidade = cobrancasPorAssinatura?.[item.assinatura.id] || 0;
                                return (
                                    <div key={item.assinatura.id} style={{ marginBottom: tokens.spacingXs }}>
                                        <strong>
                                            {item.assinatura.description || "A outra assinatura"} ({formatarParaReais(item.assinatura.value)} {textoCiclo(item.assinatura.cycle)})
                                        </strong>{" "}
                                        não vira o aluguel e continua ativa no Asaas, gerando cobranças.
                                        {quantidade > 0 && (
                                            <> {quantidade === 1 ? "O vencimento que ela já gerou está" : `Os ${quantidade} vencimentos que ela já gerou estão`} na próxima página para você trazer como lançamento.</>
                                        )}
                                    </div>
                                );
                            })}
                            <div style={{ fontSize: tokens.fontXs }}>
                                Para cobranças que não são aluguel, use o lançamento parcelado da locação. Se a assinatura não for mais usada, cancele no painel do Asaas.
                            </div>
                        </div>
                    )}

                    {selecionada && (selecionada.faturasVencidasEmAberto || 0) > 0 && (
                        <div style={{ ...info, marginTop: tokens.spacingMd }}>
                            <div style={{ fontWeight: 600, color: tokens.textPrimary }}>
                                {selecionada.faturasVencidasEmAberto === 1
                                    ? `Há 1 fatura vencida em aberto, de ${formatarParaReais(selecionada.valorFaturasVencidasEmAberto || 0)}.`
                                    : `Há ${selecionada.faturasVencidasEmAberto} faturas vencidas em aberto, somando ${formatarParaReais(selecionada.valorFaturasVencidasEmAberto || 0)}.`}
                            </div>
                            {/* Com a retirada no início da cobrança, as vencidas são parcelas em atraso DESTA
                                locação, não dívida de um contrato anterior: a pergunta passa a ser só o valor. */}
                            {selecionada.primeiroVencimentoLivre && (
                                <div style={{ marginTop: tokens.spacingXs, fontSize: tokens.fontXs, color: tokens.textMuted }}>
                                    {selecionada.faturasVencidasEmAberto === 1 ? "Ela entra" : "Elas entram"} como parcela em atraso desta locação.
                                </div>
                            )}
                            <label style={{ display: "block", marginTop: tokens.spacingSm, cursor: "pointer" }}>
                                <input
                                    type="radio"
                                    name="faturasEmAbertoAsaas"
                                    checked={!atualizarFaturas}
                                    onChange={() => onAtualizarFaturas(false)}
                                    style={{ marginRight: tokens.spacingSm }}
                                />
                                {selecionada.primeiroVencimentoLivre
                                    ? "Manter o valor que o locatário já recebeu"
                                    : "Manter como estão, é dívida do contrato anterior"}
                            </label>
                            <label style={{ display: "block", cursor: "pointer" }}>
                                <input
                                    type="radio"
                                    name="faturasEmAbertoAsaas"
                                    checked={atualizarFaturas}
                                    onChange={() => onAtualizarFaturas(true)}
                                    style={{ marginRight: tokens.spacingSm }}
                                />
                                Atualizar para o valor desta locação, muda o boleto que o locatário já recebeu
                            </label>
                        </div>
                    )}

                    {selecionada && pagasEmQuestao.length > 0 && (
                        <div style={{ ...info, marginTop: tokens.spacingMd }}>
                            <div style={{ fontWeight: 600, color: tokens.textPrimary }}>
                                {inicio
                                    ? pagasEmQuestao.length === 1
                                        ? `Há 1 cobrança paga antes do início desta locação, de ${formatarParaReais(valorPagasEmQuestao)}.`
                                        : `Há ${pagasEmQuestao.length} cobranças pagas antes do início desta locação, somando ${formatarParaReais(valorPagasEmQuestao)}.`
                                    : pagasEmQuestao.length === 1
                                        ? `Esta assinatura tem 1 cobrança paga, de ${formatarParaReais(valorPagasEmQuestao)} (${formatarData(pagasEmQuestao[0].dueDate)}).`
                                        : `Esta assinatura tem ${pagasEmQuestao.length} cobranças pagas, somando ${formatarParaReais(valorPagasEmQuestao)} (${formatarData(pagasEmQuestao[0].dueDate)} a ${formatarData(pagasEmQuestao[pagasEmQuestao.length - 1].dueDate)}).`}
                            </div>
                            {!inicio && (
                                <div style={{ marginTop: tokens.spacingXs, fontSize: tokens.fontXs, color: tokens.textMuted }}>
                                    As que vencerem a partir da data de retirada entram no histórico desta locação. E as anteriores?
                                </div>
                            )}
                            <label style={{ display: "block", marginTop: tokens.spacingSm, cursor: "pointer" }}>
                                <input
                                    type="radio"
                                    name="pagasAnterioresAsaas"
                                    checked={!importarPagasAnteriores}
                                    onChange={() => onImportarPagasAnteriores(false)}
                                    style={{ marginRight: tokens.spacingSm }}
                                />
                                Deixar de fora, pertencem ao contrato anterior
                            </label>
                            <label style={{ display: "block", cursor: "pointer" }}>
                                <input
                                    type="radio"
                                    name="pagasAnterioresAsaas"
                                    checked={importarPagasAnteriores}
                                    onChange={() => onImportarPagasAnteriores(true)}
                                    style={{ marginRight: tokens.spacingSm }}
                                />
                                Trazer para o histórico desta locação, como parcelas pagas
                            </label>
                        </div>
                    )}
                </>
            )}

            {deOutrasLocacoes.length > 0 && (
                <div style={{ ...info, marginTop: tokens.spacingMd }}>
                    <strong>Não será alterada:</strong> este cliente também tem assinatura ativa de{" "}
                    {deOutrasLocacoes.length > 1 ? "outras locações" : "outra locação"}.
                    <ul style={{ margin: `${tokens.spacingXs} 0 0 1rem`, padding: 0 }}>
                        {deOutrasLocacoes.map((item) => (
                            <li key={item.assinatura.id}>
                                {formatarParaReais(item.assinatura.value)} {textoCiclo(item.assinatura.cycle)}, veículo{" "}
                                {item.placaDaLocacaoDona || "sem placa"}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {bloqueadas.length > 0 && (
                <div style={{ ...aviso, marginTop: tokens.spacingMd }}>
                    <strong>Não pode ser aproveitada:</strong>
                    <ul style={{ margin: `${tokens.spacingXs} 0 0 1rem`, padding: 0 }}>
                        {bloqueadas.map((item) => (
                            <li key={item.assinatura.id}>
                                {formatarParaReais(item.assinatura.value)} {textoCiclo(item.assinatura.cycle)}:{" "}
                                {item.motivoBloqueioDescricao}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

interface PropsResumo {
    classificacao?: ClassificacaoAssinaturas | null;
    carregando?: boolean;
    escolha: string | null;
    onAbrir: () => void;
}

/**
 * Linha de resumo dentro do formulário de locação: diz o que foi decidido sobre a cobrança no Asaas e
 * permite reabrir a escolha. A decisão em si acontece no modal, para não competir com o cadastro.
 */
export function ResumoEscolhaAssinatura({ classificacao, carregando, escolha, onAbrir }: PropsResumo) {
    if (carregando) {
        return <div style={info}>Consultando as assinaturas deste cliente no Asaas...</div>;
    }

    if (!classificacao) return null;

    if (classificacao.consultaIndisponivel) {
        return (
            <div style={aviso}>
                Não foi possível verificar as assinaturas deste cliente no Asaas. Se ele já tiver uma assinatura
                ativa, cadastrar agora pode gerar cobrança duplicada.
            </div>
        );
    }

    const lista = classificacao.assinaturas || [];
    const reaproveitaveis = lista.filter((a) => a.origem !== OrigemAssinaturaAsaas.DeOutraLocacao && a.podeReaproveitar);
    const deOutrasLocacoes = lista.filter((a) => a.origem === OrigemAssinaturaAsaas.DeOutraLocacao);

    if (!reaproveitaveis.length && !deOutrasLocacoes.length) return null;

    const selecionada = reaproveitaveis.find((a) => a.assinatura.id === escolha);

    return (
        <div style={info}>
            {reaproveitaveis.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: tokens.spacingSm, flexWrap: "wrap" }}>
                    <span>
                        <strong>Assinatura no Asaas:</strong>{" "}
                        {selecionada
                            ? `aproveitar a que já existe (${formatarParaReais(selecionada.assinatura.value)} ${textoCiclo(selecionada.assinatura.cycle)})`
                            : "criar uma nova"}
                    </span>
                    <button
                        type="button"
                        onClick={onAbrir}
                        style={{
                            padding: "2px 12px",
                            borderRadius: tokens.radiusSm,
                            cursor: "pointer",
                            border: `1px solid ${tokens.primaryColor}`,
                            background: tokens.bgWhite,
                            color: tokens.primaryColor,
                            fontSize: tokens.fontXs,
                        }}
                    >
                        alterar
                    </button>
                </div>
            )}
            {deOutrasLocacoes.length > 0 && (
                <div style={{ marginTop: reaproveitaveis.length ? tokens.spacingXs : 0, fontSize: tokens.fontXs, color: tokens.textMuted }}>
                    Assinatura de {deOutrasLocacoes.length > 1 ? "outras locações" : "outra locação"} (
                    {deOutrasLocacoes.map((d) => d.placaDaLocacaoDona || "sem placa").join(", ")}) não será alterada.
                </div>
            )}
        </div>
    );
}
